/**
 * D-EZ-111 — THE CALLS: WHAT EACH SEAT SAYS, IN WHAT REGISTER.
 *
 * Banks of lines with `{slots}`. Every slot is filled from the race or the
 * league by `broadcast.ts`, which also writes each value down as a Fact — so
 * the bank can be as lively as it likes and still say nothing the dice did not.
 *
 * REGISTER IS THE POINT. Howard: "the commentary sucks". Part of it was that
 * every moment was called in the same flat sentence. A booth has levels:
 *
 *   1 calm   — an early lead change, a routine stop. One short line.
 *   2 lively — a caution, a mid-race lead change, a penalty.
 *   3 peak   — a late lead change, a big crash, the leader out, the finish.
 *              The play-by-play voice goes big, in short bursts.
 *
 * And each play-by-play voice has a style (`HostStyle`) — the big voice, the
 * straight caller, the dry one, the porch-talker — so two leagues sound like two
 * different broadcasts. People talk in contractions; so does this.
 *
 * Slots used below: {A}/{B} full names, {a}/{b} short names (the surname, or
 * the full name when two in the field share it), {togo} "six to go",
 * {Togo} "Six to go", {pos} a spoken position ("fourth", "14th"), {n} a count.
 */

import type { HostStyle } from './cast.ts';

type Bank = readonly string[];
type ByStyle = Record<HostStyle, Bank>;

const both = (shared: Bank, style: ByStyle): ByStyle => ({
  big: [...shared, ...style.big],
  straight: [...shared, ...style.straight],
  dry: [...shared, ...style.dry],
  folksy: [...shared, ...style.folksy],
});

/** A lead change before the closing laps. Then {drop}. */
export const LEAD: ByStyle = both(
  [
    '{A} takes the lead from {B}.{drop}',
    '{A} goes by {B} for the lead.{drop}',
    "New leader, and it's {A}.{drop}",
    '{A} moves to the front, past {B}.{drop}',
    '{b} gives it up. {A} leads.{drop}',
    '{A} takes over at the front.{drop}',
    "{a}'s out front now, past {B}.{drop}",
  ],
  {
    big: ["And here comes {a}! He's by {B} for the lead.{drop}", "{A} to the front! {b} couldn't hold him.{drop}", '{A} takes the lead!{drop}', 'There goes {a}! New leader!{drop}'],
    straight: ['{A} gets the lead away from {B}.{drop}', "That's a lead change. {A} past {B}.{drop}", '{A} wins the fight for the top spot with {B}.{drop}', '{A} is your new leader.{drop}'],
    dry: ['{A} leads now.{drop}', '{a} past {b}. New leader.{drop}', 'Lead changes hands. {A}.{drop}', "{b} out, {a} in. That's the lead.{drop}"],
    folksy: ["Well, look at {a}. He's gone by {B} for the lead.{drop}", 'There goes {a} to the front, right past {B}.{drop}', '{A} takes it from {B}, and good for him.{drop}', "Hey now, {a}'s leading this thing.{drop}"],
  },
);

/** The leader had trouble and fell back — nobody passed him. Then {drop}. */
export const INHERIT: Bank = [
  'Trouble for the leader. {B} slides back, and {A} takes over.{drop}',
  '{b} has a problem up front, and {A} inherits the lead.{drop}',
  '{A} is the new leader after {b} runs into trouble.{drop}',
  'The leader is in trouble! {A} takes over from {B}.{drop}',
  'The day takes a turn for {b}, and {A} leads.{drop}',
  '{A} finds himself in the lead as {b} falls back.{drop}',
  "That's not a pass. {B} drops back and {A} takes over.{drop}",
];
export const INHERIT_LATE: Bank = [
  'Trouble for the leader! {B} drops back, and {A} leads, {togo}!',
  '{b} is in trouble! {A} inherits the lead, {togo}!',
  "Oh, the leader's in trouble! {A} takes over, {togo}!",
  "{A} leads, {togo}, and {b} is going backwards!",
  'Disaster for {b}! {A} is the new leader, {togo}!',
];
/** The lead changed without a pass on the books. Then {drop}. */
export const NEUTRAL: Bank = [
  '{A} takes the lead.{drop}',
  'New leader: {A}.{drop}',
  '{A} leads now.{drop}',
  '{A} is the new leader.{drop}',
  '{A} takes over at the front.{drop}',
  'The lead goes to {A}.{drop}',
  '{A} moves to the top of the order.{drop}',
];
export const NEUTRAL_LATE: Bank = [
  'New leader, {togo}: {A}!',
  '{A} takes the lead, {togo}!',
  "{A}'s out front now, {togo}.",
  'The lead goes to {A}, {togo}.',
  "{Togo}, and it's {A} leading.",
];

/** Where the old leader went. */
export const DROP: Bank = [' {b} drops to {pos}.', ' {b} falls back to {pos}.', " {b}'s down to {pos}.", ' That puts {b} {pos}.', ' {b} slides to {pos}.', ' {b} goes back to {pos}.'];
export const DROP_SECOND: Bank = [' {b} slots in second.', ' {b} settles for second.', " {b}'s right there in second.", ' {b} drops to second.'];

/** A lead change late. */
export const LATE_LEAD: ByStyle = {
  big: [
    "He's done it! {A} takes the lead from {B}, {togo}!",
    "Here's {a}! He's by {b} for the lead, {togo}!",
    '{A} to the lead! {Togo}, and {b} has to answer!',
    'Lead change! {A}! {Togo}!',
    "{A} goes to the front! {Togo}! Can {b} come back?",
    "Look out! {a}'s by {b}, {togo}!",
  ],
  straight: [
    '{A} takes the lead from {B}, {togo}.',
    'Lead change late: {A} past {B}, {togo}.',
    '{A} goes to the front, {togo}. {b} has work to do.',
    '{Togo}, and {A} has the lead from {B}.',
    '{A} gets by {B} for the lead. {Togo}.',
    'A big pass for the lead: {A} over {B}, {togo}.',
  ],
  dry: ["And now it's {a}. {Togo}.", '{a} past {b}. {Togo}. Your move, {b}.', '{A} leads. {Togo}.', "New leader, {togo}. It's {A}.", '{Togo}. {a} has it. {b} wants it back.', '{a} takes it from {b}. {Togo}.'],
  folksy: [
    "Oh, here we go! {A}'s by {B}, {togo}!",
    'Would you look at that. {A} leads, {togo}!',
    '{A} just went and took it from {B}. {Togo}!',
    'Hang on to something. {A} has the lead, {togo}!',
    "Well I'll be. {a}'s out front, {togo}!",
    "{A}! He's gone by {b}, and there's {togo}!",
  ],
};

/** A lead change on the last turn of the scheduled distance. */
export const FINAL_LEAD: ByStyle = {
  big: ['{A} takes the lead on the final turn!{drop}', "At the very end, it's {a}! He's by {B}!{drop}"],
  straight: ['{A} takes the lead from {B} on the final turn.{drop}', 'On the last turn, {A} gets by {B} for the lead.{drop}'],
  dry: ['Last turn. {a} past {b}.{drop}', 'Final turn, new leader: {A}.{drop}'],
  folksy: ["Oh my, on the last turn! {A}'s by {B}!{drop}", 'Right at the end, {a} goes and gets it from {b}!{drop}'],
};

/** The caution, nobody hurt much. */
export const CAUTION: ByStyle = both(
  ["The caution's out.", 'Yellow flag.', 'Trouble, and out comes the caution.', 'Here comes the yellow.'],
  {
    big: ['Trouble! The yellow is out!', 'Caution! Somebody is in trouble!'],
    straight: ['The yellow flag is out.', 'Caution flag on the field.'],
    dry: ['Yellow.', 'Caution.'],
    folksy: ['Uh oh. Here comes the yellow.', 'Somebody found trouble, and here is the caution.'],
  },
);
/** The caution, a few caught up. Slots: n (2 or more). */
export const CAUTION_N: Bank = ['Caution. {n} cars caught up in it.', 'The yellow is out, {n} cars involved.', 'Trouble for {n} cars, and the caution flies.'];

/** A big crash: three or more caught up. */
export const BIG_CRASH: ByStyle = {
  big: ['Big crash! {n} cars caught up and the yellow is out!', "They're crashing! {n} cars involved!", 'Here it comes! {n} cars caught up in it!'],
  straight: ['A multi-car crash, {n} caught up in it. Caution.', '{n} cars caught up in one crash. The yellow is out.', 'Big trouble: {n} involved.'],
  dry: ['Multi-car crash. {n} involved.', '{n} cars. One crash. Caution.', "That's {n} caught up in it."],
  folksy: ["Oh, that's a mess. {n} cars caught up in it.", 'Lord have mercy, {n} cars in one crash.', "That's going to hurt a lot of people. {n} involved."],
};

/** Who is out. Slots: names, verb ("is"/"are"). */
export const OUT: Bank = [' {names} {verb} out of the race.', ' That ends the day for {names}.', ' {names} {verb} done for the day.', " {names} won't finish.", ' That is all for {names}.', ' {names} {verb} finished for today.', ' No checkered flag today for {names}.'];

/** The leader out. */
export const LEADER_OUT: ByStyle = {
  big: ["The leader is out! {L} is done!", "{L} was leading, and he's out of the race!"],
  straight: ["The leader is out of the race. {L} is done.", '{L} was leading, and his day is over.'],
  dry: ['{L}. Leading. Out.', "That's the leader, {L}, out of it."],
  folksy: ["Oh no. That's the leader. {L} is out.", 'Heartbreaker. {L} was leading, and now he is done.'],
};

/** Under yellow, who leads. */
export const UNDER_YELLOW: Bank = [
  ' {L} leads under yellow.',
  ' {L} leads the field under caution.',
  ' {L} holds the lead, {S} second.',
  ' {L} still leads.',
  ' {L} out front for the restart, {S} behind him.',
  " It's {L} leading, {S} second.",
  ' {L} keeps the top spot.',
  " {L}'s the leader under yellow.",
  ' {L} will lead them back to green.',
  ' Under the caution, {L} is first and {S} second.',
  ' {L} is still in charge.',
];

/** A restart late. */
export const RESTART: ByStyle = {
  big: ['Green flag! {Togo}! {L} leads them away!', 'Back to green! {L} out front, {S} second, {togo}!', "We're racing again! {Togo}, {L} leads!"],
  straight: ['Green flag, {togo}. {L} leads, {S} second.', 'Back to green, {togo}. {L} leads them away.', 'Racing resumes, {togo}. {L} on top.'],
  dry: ['Green. {Togo}. {L} leads.', 'Racing again. {L}, then {S}. {Togo}.', 'Green flag. {L}. {Togo}.'],
  folksy: ['Here we go again, {togo}, {L} leading the way!', 'Back to racing! {L} in front, {S} right behind, {togo}.', "Green flag's out, {togo}. {L} on point."],
};

/** Late, and the order holds. */
export const RUN_TO_FLAG: ByStyle = {
  big: ['{Togo}! {L} leads, {S} second!', '{L}, {togo}! {S} still second!', "{Togo}, and it's still {L}!"],
  straight: ['{Togo}. {L} leads, {S} second.', '{L} still leads, {togo}, {S} second.', "{Togo}, and {L}'s still out front."],
  dry: ['Still {L}. {Togo}.', '{Togo}. {l}, then {s}.', '{L}. {Togo}. Nothing changes.'],
  folksy: ["{Togo}, and it's still {L} out front.", '{L} is almost home. {Togo}, {S} second.', '{Togo}. {l} just needs to bring it home.'],
};

/** The leader holds off a late challenge. */
export const HELD: ByStyle = {
  big: ['{C} goes for it, and {l} holds him off!', '{l} slams the door on {c}!', 'Not today! {l} keeps {c} behind him!'],
  straight: ["{C} challenges {L} and can't make it stick.", '{l} holds off {c}.', '{c} tries {l} for the lead. No dice.'],
  dry: ['{c} tries. {l} says no.', 'Not this time, {c}.', '{c} looks. {l} keeps it.'],
  folksy: ['{c} took a look, and {l} was having none of it.', '{c} went for it, but {l} held the door shut.', "{l}'s not giving that up to {c}. No sir."],
};

/** The finish. Slots: W S race track. */
export const FINISH: ByStyle = {
  big: ['{W} wins it! {W} wins the {race}!', 'Checkered flag! {W} wins at {track}!', '{W}! He wins the {race}! {S} second!'],
  straight: ['Checkered flag. {W} wins the {race}, {S} second.', '{W} wins the {race}. {S} runs second.', '{W} takes the checkered flag at {track}. {S} second.'],
  dry: ['{W}. Wins it. {S} second.', 'Checkered flag: {W}. {S} second.', "{W} wins the {race}. That's the race."],
  folksy: ['How about that! {W} wins the {race}!', "{W}'s your winner at {track}, and {S} is second.", "Checkered flag, and it's {W}! {S} second."],
};

/** Pit road, leader keeps it. */
export const PIT_HOLD: Bank = [
  '{L} keeps the lead through the stops.',
  "The stops don't change the front. {L} still leads.",
  '{L} comes off pit road still in front.',
  "Clean work down here. {L}'s still the leader.",
  '{L} gets in and out, and keeps the lead.',
  'No change at the front through the stops. {L} leads.',
  '{L} is first off pit road again.',
  'The leader, {L}, keeps it through pit road.',
];
export const PIT_HOLD_TEAM: Bank = ['{L} keeps the lead through the stops, and the {team} crew gets him back out first.', 'Good work by the {team} crew. {L} is still in front.', "The {team} crew gets {L} out first. He's still the leader."];
/** Pit road, the lead changes there. */
export const PIT_LEAD: Bank = [
  '{A} comes off pit road first, ahead of {B}.',
  '{A} beats {B} off pit road and takes the lead.',
  'The lead changes hands on pit road: {A} out first, {B} behind him.',
  '{A} wins the race off pit road. {B} gives up the lead.',
  "{A}'s first out of the pits, and {B} loses the lead down here.",
  'A new leader off pit road: {A}. {B} gives it up in the stops.',
];
export const PIT_LEAD_TEAM: Bank = ['The {team} crew wins it on pit road: {A} out first, {B} behind him.', '{A} takes the lead off pit road, a big stop by the {team} crew.', 'Huge stop by the {team} crew. {A} leads, {B} loses the spot.'];
export const PIT_MOVER: Bank = [' {M} gains the most down here, up to {to} from {from}.', ' Big stop for {M}: {to} now, from {from}.', ' {M} comes out {to}, up from {from}.', ' {M} picks up spots in the pits, {from} to {to}.', ' Nobody gained more down here than {M}, {from} to {to}.', ' Watch {M}: in {from}, out {to}.', ' {M} gains on pit road and comes out {to}.'];
export const PIT_PENALTY: Bank = ['{P} is caught for a pit-road violation and drops to {pos}.', "Penalty on {P}: a pit-road violation, and he's back to {pos}.", 'Bad news for {P}. Pit-road violation, down to {pos}.'];

/** Pit road hands out a performance chip. Slots: names verb bank. */
export const CHIP_EARNED: Bank = [
  '{names} {verb} a performance chip from the stop — an advantage available for a later contest.',
  '{names} {bank} a performance chip on that stop. Something to spend later.',
  'A good stop earns {names} a performance chip for later.',
  'A chip earned on pit road for {names}, and that can matter later.',
  '{names} {verb} a performance chip down here. Keep that in your back pocket.',
];

/** The pit reporter with a word from a driver who is out. Slots: s q. */
export const QUOTE: Bank = ['I caught {s} for a moment: "{q}"', '{s} had this to say: "{q}"', 'Here\'s {s}: "{q}"', 'A quick word from {s}: "{q}"', 'I asked {s} about it: "{q}"'];

/** Retirement reasons, as pit road says them. Slots: N. */
export const REASON: Record<string, Bank> = {
  trouble: ['That crash ends the day for {N}.', "{N}'s out after the trouble."],
  problem: ['For {N}, a car problem ends the race.', "{N}'s out with a problem on the car."],
  fuel: ["{N} is out of fuel, and that's his day.", "No fuel left for {N}. He's done."],
  'black-flag': ['{N} has been black-flagged out of the race.', 'A black flag ends it for {N}.'],
  'repair-failed': ["{N} tried to fix it, and the repair didn't take. He's out.", 'The repair fails for {N}. Out of the race.'],
  injury: ["{N} is hurt and can't continue.", 'Injury ends the race for {N}.'],
  feud: ['The feud ends it for {N}.', "{N}'s out, and the feud did it."],
};

/** Minor retirements gathered up. Slots: names verb. */
export const ROUNDUP: Bank = ['Also out of it: {names}.', "We've also lost {names}.", 'Elsewhere, {names} {verb} out of the race.', 'Behind the leaders, {names} {verb} done for the day.'];
export const CLOSE_ROUNDUP: Bank = ['Out early today: {names}.', "Didn't make the finish: {names}."];

/** The open. Slots: race track field P. */
export const WELCOME: ByStyle = {
  big: [
    "It's race day at {track}! Welcome to the {race}!",
    "Welcome to {track} and the {race}! {field} cars, and we're ready!",
    "Here we go! The {race} from {track}, and {P} leads them to the green!",
    "{field} cars, one checkered flag! Welcome to the {race} at {track}!",
  ],
  straight: [
    'Welcome to the {race} at {track}. {field} cars take the green, {P} on the pole.',
    'Good to have you with us for the {race} from {track}. {P} starts on the pole.',
    "It's the {race} at {track}, and {P} has the pole.",
    'From {track}, this is the {race}. {field} starters, {P} up front.',
  ],
  dry: [
    '{race}. {track}. {field} cars. {P} on the pole.',
    "The {race}, from {track}. Let's get to it.",
    '{track}. The {race}. {P} on the pole.',
    'Welcome to {track}. {field} cars. One race.',
  ],
  folksy: [
    "Well, pull up a chair. It's the {race} at {track}, and {field} cars are ready to go.",
    'Howdy, everybody, and welcome to {track} for the {race}.',
    "Glad you're with us. It's the {race}, and {P} is sitting on the pole.",
    "We're at {track} for the {race}, and folks, it's a good one to be at.",
  ],
};

/** Where we are in the year. Slots: n N. */
export const WHERE_IN_YEAR: Bank = [' Race {n} of {N}.', ' This is race {n} of {N}.', " We're at race {n} on the calendar."];

/** The sign-off. Slots: H a p next. */
export const SIGN_OFF: Bank = [
  "For {a} and {p}, I'm {H}. Next up, the {next}.",
  "That'll do it. {a}, {p}, thank you. We'll see you at the {next}.",
  "For everybody in the booth and on pit road, I'm {H}. The {next} is next.",
];
export const SIGN_OFF_LAST: Bank = ["For {a} and {p}, I'm {H}. Thanks for being with us.", "That's the race. {a}, {p}, thank you. I'm {H}."];

/**
 * Capitalised words the banks above (and the analyst's and the storylines')
 * use that are not names — the grounding gate's allow-list. The test proves
 * none of them is a name the field generator can deal out, so this list can
 * never hide a driver.
 */
export const BOOTH_WORDS: readonly string[] = [
  "That's", "He's", "It's", "Here's", "There's", "Let's", "We're", "We've", "I'll", "I'm", "That'll", "Didn't", "Can't", "Don't",
  "Leader's", "Green", "Caution", "Penalty", "Checkered", "Lead", "New", "Trouble", "Uh", "Multi-car", "Lord", "Hang", "Oh",
  "Would", "Heartbreaker", "Racing", "Not", "How", "Bad", "Clean", "Good", "Also", "Elsewhere", "Howdy", "Welcome", "Thanks",
  "Keep", "Watch", "Give", "Put", "My", "Called", "Experience", "Ride", "Temper", "Restarts", "Races", "Points", "Round",
  "Hey", "Huge", "Behind", "Remember", "Under", "Let", "Somebody", "Look", "Lead", "Playoff", "Last", "Final", "Right",
  "Big", "Yellow", "Pit", "Moving", "Close", "Write", "Told", "Can", "Hard", "Quick", "Plenty", "Write", "Called", "Seventeen",
  "Pull", "Nothing", "Somebody", "Huge", "No", "Out", "Did", "Still", "Down", "Back", "Coming", "One", "Who", "Second",
  "Kept", "Want", "Ask", "Asked", "Check", "Do", "Did", "Was", "Last", "Twice", "Done", "Nope", "Sir", "Me", "Some",
  "Wins", "Next", "Call", "Leading", "Score", "Patience", "We'll", "I've", "Disaster", "Glad", "Points", "Stubborn",
  "Same", "Why", "Fool", "Checking", "Wrecking", "Hot", "Photo", "Won", "Standings", "Leans", "They're", "They've", "Injury", "Mostly", "Knocking", "Since", "Plenty", "Today", "Tied", "Neither", "Bragging", "Round", "Final",
];
